import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { reload } from 'firebase/auth';
import EmailVerification from './EmailVerification';
import { Loader2, Shield } from 'lucide-react';
import { trackEmailVerified } from '../../utils/pixels';
import { getBrazilianStatus } from '../../utils/detectBrazil';

const EmailVerificationGuard = ({ user, children }) => {
  const [loading, setLoading] = useState(true);
  const [emailVerified, setEmailVerified] = useState(false);

  useEffect(() => {
    const checkEmailVerification = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // If already verified, skip reload and just set state
        if (user.emailVerified) {
          setEmailVerified(true);
          setLoading(false);
          return;
        }

        // Only refresh if not verified
        await reload(user);

        // Log the verification check (only when not already verified)
        console.log('📧 Email verification check:', {
          uid: user.uid,
          email: user.email,
          emailVerified: user.emailVerified
        });

        setEmailVerified(user.emailVerified);
      } catch (error) {
        console.error('❌ Error checking email verification:', error);
        // On error, assume not verified to be safe
        setEmailVerified(false);
      } finally {
        setLoading(false);
      }
    };

    checkEmailVerification();
  }, [user]);

  // If no user, redirect to login
  if (!user) {
    return <Navigate to="/login" />;
  }

  // Show loading while checking verification status
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050311]">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="mx-auto h-12 w-12 bg-emerald-500/20 rounded-full flex items-center justify-center mb-4">
            <Shield className="h-6 w-6 text-emerald-400" />
          </div>
          <Loader2 className="h-8 w-8 text-emerald-400 animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Checking Email Status</h3>
          <p className="text-gray-400 text-sm">Verifying your account...</p>
        </div>
      </div>
    );
  }

  // If email not verified, show verification component
  if (!emailVerified) {
    return (
      <EmailVerification
        user={user}
        onVerificationComplete={() => {
          // Gather context for pixel tracking (same pattern as trackSignup)
          const landingPage = sessionStorage.getItem('landingPage');
          const signupMethod = user.providerData?.[0]?.providerId === 'google.com' ? 'google' : 'email';
          const isBrazilian = getBrazilianStatus();

          // Track email verification completion with context
          trackEmailVerified(user.uid, {
            language: isBrazilian ? 'BRL' : 'ENG',
            fromLandingPage: landingPage,
            signupMethod: signupMethod
          });

          // Clean up localStorage flag
          const sentKey = `email_verification_sent_${user.uid}`;
          localStorage.removeItem(sentKey);
          // Reload the page to refresh user state
          console.log('✅ Email verification completed - reloading page');
          window.location.reload();
        }}
      />
    );
  }

  // Email verified, render children
  return children;
};

export default EmailVerificationGuard;
